import React, { Component } from "react";

import PDFMerger from "pdf-merger-js/browser";
import { toast } from "react-toastify";

import HorizontalScroll from "../../../../Common/HorizontalScroll/HorizontalScroll";
import PdfPreviewModal from "../../../../Common/UIComponents/PdfPreviewModal";
import { flattenCoaFilePaths } from "../ReportSpecs/helper";
import PreviewDatePicker from "./PreviewDatePicker";

import { downloadFileFromS3WithPresigned, getFileFromS3WithPresigned } from "../../../../../utils/helpers";

const PREVIEW_TAB_SEPARATOR = "::";

const buildPreviewTabKey = (date, category) => `${date}${PREVIEW_TAB_SEPARATOR}${category}`;

export default class ReportPreviewModal extends Component {
  constructor(props) {
    super(props);

    const { filePaths, previewUrlList } = props;
    if (!filePaths || Object.keys(filePaths).length < 1) {
      this.state = {
        loadingFiles: false,
        previewUrlList: previewUrlList || [{ url: null }],
        activeDate: previewUrlList?.[0]?.date || null,
        categories: {},
        selectedCategory: null,
        reportDates: [],
        downloadingCoa: false,
        previewAvailable: Boolean(previewUrlList && previewUrlList.length),
      };
      return;
    }

    const recentEntries = this.getSortedFileEntries(filePaths);
    const categories = this.setCategories(recentEntries);
    const activeDate = recentEntries[0][0];
    const selectedCategory = categories[activeDate][0];
    const reportDates = recentEntries.map((entry) => ({ date: entry[0] }));

    this.state = {
      loadingFiles: true,
      previewUrlList: [{ url: null }],
      activeDate,
      categories,
      selectedCategory,
      reportDates,
      downloadingCoa: false,
      previewAvailable: false,
    };
  }

  componentDidMount() {
    const { filePaths } = this.props;
    if (filePaths && Object.keys(filePaths).length > 0) {
      this.handleFetchAndMergeFiles();
    }
  }

  setCategories = (fileEntries) => fileEntries.reduce((acc, entry) => {
    acc[entry[0]] = Object.keys(entry[1]);
    return acc;
  }, {});

  handleFetchAndMergeFiles = () => {
    const { filePaths } = this.props;
    if (!filePaths || Object.keys(filePaths).length < 1) {
      toast.error("Unable to preview, no valid file path for this report!");
      this.setState({ loadingFiles: false, previewAvailable: false });
      return;
    }

    this.setState({ loadingFiles: true, previewAvailable: false });
    const recentEntries = this.getSortedFileEntries(filePaths);
    const files = recentEntries.map((entry) => {
      const [date, data] = entry;
      return Object.entries(data).map((f) => [date, ...f]);
    }).flat();

    Promise.all(files.map(async (fileData) => {
      const [date, category, filepaths] = fileData;
      const fileTempArr = Array.from(new Set(filepaths));
      return Promise.all(fileTempArr.map(async (fileTemp) => this.getFileFromAWS(fileTemp))).then(async (allPreviewFiles) => {
        const successfullyFetched = allPreviewFiles.filter((file) => file?.file);
        if (!successfullyFetched.length) throw new Error("Failed to fetch files");
        return this.mergePDFs(successfullyFetched, date, category);
      });
    })).then((mergedPreviewFiles) => {
      const previewUrlList = mergedPreviewFiles;
      this.setState({
        loadingFiles: false,
        previewUrlList,
        previewAvailable: true,
      });
    }).catch((error) => {
      this.setState({
        loadingFiles: false,
        previewAvailable: false,
      });
      toast.error(error.message || "Preview not available.");
    });
  };

  getSortedFileEntries = (filePaths) => {
    const entriesByDate = Object.entries(filePaths);
    return entriesByDate.sort((a, b) => Date.parse(b[0]) - Date.parse(a[0]));
  }

  /* Fetches file from correct (private or public) s3 bucket. Returns blob if successful, null otherwise. */
  getFileFromAWS = async (fileTemp) => {
    const privateS3Bucket = fileTemp.includes("COAs/");
    const fileName = fileTemp.split("/").pop();
    const fileBlobObj = await getFileFromS3WithPresigned([fileTemp], privateS3Bucket ? "private" : "public");
    if (fileBlobObj?.[fileTemp]) {
      return { fileName, file: fileBlobObj[fileTemp] };
    }
    return null;
  };

  /* if there are multiple pdfs for a single date in preview, merge into one pdf */
  mergePDFs = async (fileBlobs, date, category) => {
    /** Merge */
    if (fileBlobs.length > 1) {
      const merger = new PDFMerger();
      const fileNames = [];
      return Promise.all(fileBlobs.map(async ({ fileName, file }) => { // eslint-disable-line
        fileNames.push(fileName);
        return merger.add(file);
      })).then(async () => { // eslint-disable-line
        const mergedPdf = await merger.saveAsBlob();
        const FileTemp = new File([mergedPdf], "name");
        const pdfURL = window.URL.createObjectURL(mergedPdf);
        const filesObj = {
          url: pdfURL,
          privateS3: true,
          file: FileTemp,
          blob: mergedPdf,
          name: fileNames.join(),
          date,
          category,
        };
        return filesObj;
      });
    }

    /** Don't need to merge if there's only one pdf */
    if (fileBlobs.length === 1) {
      const { fileName, file } = fileBlobs[0];
      const FileTemp = new File([file], "name");
      const pdfURL = window.URL.createObjectURL(file);
      const filesObj = {
        url: pdfURL,
        privateS3: true,
        file: FileTemp,
        blob: file,
        name: fileName,
        date,
        category,
      };
      return filesObj;
    }

    const filesObj = {
      url: "",
      date,
    };

    return filesObj;
  };

  handleDateChanged = (date) => {
    this.setState((state) => ({
      activeDate: date,
      selectedCategory: state.categories[date][0],
    }));
  }

  handleCategoryChange = (category) => {
    this.setState({
      selectedCategory: category,
    });
  }

  handleDownloadCoa = async () => {
    const { filePaths } = this.props;
    const filePathList = flattenCoaFilePaths(filePaths);

    if (filePathList.length === 0) {
      toast.error("Unable to download, no valid file path for this report!");
      return;
    }

    this.setState({ downloadingCoa: true });

    try {
      const downloadResults = await Promise.allSettled(
        filePathList.map((filePath) => {
          const fileName = filePath.split("/").pop() || "";
          const isPrivateS3Bucket = filePath.includes("COAs/");
          return downloadFileFromS3WithPresigned(
            [filePath],
            isPrivateS3Bucket ? "private" : "public",
            [fileName],
          );
        }),
      );

      const successCount = downloadResults.filter(
        (result) => result.status === "fulfilled" && result.value,
      ).length;

      if (successCount === filePathList.length) {
        toast.success(`Successfully downloaded ${successCount}/${filePathList.length} files.`);
      } else if (successCount > 0) {
        toast.warning(`Downloaded ${successCount}/${filePathList.length} files.`);
      } else {
        toast.error("Failed to download COA");
      }
    } catch (error) {
      toast.error(error.message || "Failed to download COA");
    } finally {
      this.setState({ downloadingCoa: false });
    }
  };

  render() {
    const {
      handleCloseForPreview,
      showDownloadButton = true,
      filePaths,
      previewUrlList: providedPreviewUrlList,
    } = this.props;
    const {
      activeDate,
      previewUrlList,
      loadingFiles,
      categories,
      selectedCategory,
      reportDates,
      downloadingCoa,
      previewAvailable,
    } = this.state;

    const sourcePreviewList = providedPreviewUrlList || previewUrlList;
    const dateCategories = (activeDate && categories[activeDate]) || [];
    const hasMultipleCategories = !loadingFiles && dateCategories.length > 1;

    let activePreviewItem = sourcePreviewList[0] || {};
    if (!providedPreviewUrlList && selectedCategory && activeDate) {
      activePreviewItem = previewUrlList.find(
        (file) => file.date === activeDate && file.category === selectedCategory,
      ) || {};
    }

    const previewFiles = [{
      key: providedPreviewUrlList
        ? "0"
        : buildPreviewTabKey(activeDate, selectedCategory || "coa"),
      fileName: activePreviewItem.name,
      src: activePreviewItem.url,
    }];

    const canDownload = previewAvailable && (
      filePaths
        ? flattenCoaFilePaths(filePaths).length > 0
        : Boolean(sourcePreviewList[0]?.url)
    );

    const hasMultipleDates = Object.keys(categories).length > 1;
    const headerExtraContent = (
      <>
        {hasMultipleDates && (
          <PreviewDatePicker
            data={reportDates}
            activeDate={activeDate}
            updateActiveDate={this.handleDateChanged}
          />
        )}
        {hasMultipleCategories && (
          <HorizontalScroll containerClassName="PdfPreviewModal__CategoryList">
            {dateCategories.map((category) => (
              <span
                key={category}
                onClick={() => this.handleCategoryChange(category)}
                className={[
                  "PdfPreviewModal__CategoryListItem",
                  category === selectedCategory
                    ? "PdfPreviewModal__CategoryListItem--selected"
                    : "",
                ].filter(Boolean).join(" ")}
              >
                {category}
              </span>
            ))}
          </HorizontalScroll>
        )}
      </>
    );

    return (
      <PdfPreviewModal
        open
        title="COA"
        onClose={handleCloseForPreview}
        files={previewFiles}
        loading={loadingFiles}
        showDownload={showDownloadButton && canDownload}
        showPrint={false}
        downloadLabel="Download COA"
        downloadLoading={downloadingCoa}
        downloadDisabled={!canDownload || downloadingCoa}
        onDownload={filePaths ? this.handleDownloadCoa : undefined}
        errorMessage="Preview not available."
        width="40vw"
        headerExtra={(hasMultipleDates || hasMultipleCategories) ? headerExtraContent : null}
      />
    );
  }
}
